import { useEffect, useRef, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

const gbp = (n) => "£" + Math.round(Number(n || 0)).toLocaleString("en-GB");
const TYPE_DOT = { prospect: "#2E6BB0", booked: "#C79A3B", current: "#1F6B3B", dead: "#C0392B" };
const TYPE_LABEL = { prospect: "Prospect", booked: "Booked", current: "Current client", dead: "Dead lead" };

export function GlobalSearch({ onOpenBooking, onOpenQuote }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setItems([]); setOpen(false); return undefined; }
    setBusy(true);
    const t = setTimeout(() => {
      api.get(`/admin/search?q=${encodeURIComponent(q.trim())}`)
        .then(r => { setItems(r.data.items || []); setOpen(true); })
        .catch(() => {})
        .finally(() => setBusy(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (it) => {
    setOpen(false); setQ("");
    if (it.kind === "booking") onOpenBooking(it.reference);
    else onOpenQuote(it.id);
  };

  return (
    <div className="relative" ref={boxRef}>
      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
      <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (items.length) setOpen(true); }}
        placeholder="Find a client…"
        className="w-44 md:w-64 border border-[#E5E7EB] pl-8 pr-7 py-2 text-sm bg-white focus:border-[#C79A3B] outline-none" />
      {busy && <Loader2 size={13} className="animate-spin absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />}
      {open && (
        <div data-testid="global-search-results" className="absolute left-0 top-full mt-1 w-[340px] max-h-[380px] overflow-y-auto bg-white border border-[#E5E7EB] shadow-lg z-50">
          {items.length === 0 ? (
            <div className="text-sm text-[#9CA3AF] italic text-center py-5">No clients match &ldquo;{q}&rdquo;.</div>
          ) : items.map(it => (
            <button key={`${it.kind}-${it.id}`} data-testid={`global-search-result-${it.id}`} onClick={() => pick(it)}
              className="block w-full text-left px-3 py-2.5 border-b border-[#F0EAE0] hover:bg-[#FBF3EC]">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: TYPE_DOT[it.type] || "#6B7280" }} title={TYPE_LABEL[it.type]} />
                <span className="text-sm font-bold text-[#12263A] truncate">{it.name || it.reference}</span>
                <span className="ml-auto text-xs tabular-nums text-[#6B7280] shrink-0">{gbp(it.total_inc_vat)}</span>
              </div>
              <div className="text-[11px] text-[#6B7280] mt-0.5 ml-4 truncate">
                {it.reference} · {TYPE_LABEL[it.type] || it.type}{it.postcode ? ` · ${it.postcode}` : ""}{it.email ? ` · ${it.email}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
